/**
 * quick-buy.js — Quick-buy Web Component for product cards.
 *
 * Wraps the ATC button on each product card. On click:
 *   - Single-variant products: adds to cart immediately.
 *   - Products with sizes: shows a size-selector popup filtered to the
 *     card's current colour. Selecting a size adds to cart and opens
 *     the cart drawer via TRYEvents.
 *
 * Depends on: component.js (TRYComponent), events.js (TRYEvents)
 */

const QB_COLOR_NAMES = ['color', 'colour', 'farge', 'farve'];
const QB_SIZE_NAMES  = ['size', 'størrelse', 'storlek'];

class QuickBuy extends TRYComponent {
  connectedCallback() {
    super.connectedCallback();
    if (this._initialized) return; // guard against re-entry when element is moved in DOM
    this._initialized = true;

    this._variants  = null;   // lazy-parsed from embedded JSON
    this._popup     = null;   // lazy-created DOM node
    this._adding    = false;  // in-flight cart request guard

    this._hasSizes  = this.dataset.hasSizes  === 'true';
    this._hasColors = this.dataset.hasColors === 'true';
    this._colorOption = parseInt(this.dataset.colorOption, 10);
    this._sizeOption  = parseInt(this.dataset.sizeOption,  10);
    this._firstColor  = this.dataset.firstColor;

    this._boundAtcClick = this._onAtcClick.bind(this);
    this._boundDocClick = this._onDocClick.bind(this);
    this._boundKeyDown  = this._onKeyDown.bind(this);

    this.getRequired('.product-card__atc').addEventListener('click', this._boundAtcClick);
    document.addEventListener('click', this._boundDocClick, true);
    document.addEventListener('keydown', this._boundKeyDown, true);
  }

  disconnectedCallback() {
    const atcBtn = this.querySelector('.product-card__atc');
    if (atcBtn) atcBtn.removeEventListener('click', this._boundAtcClick);
    document.removeEventListener('click', this._boundDocClick, true);
    document.removeEventListener('keydown', this._boundKeyDown, true);
    this._popup?.remove();
    this._popup = null;
  }

  // ── Event handlers ───────────────────────────────────────────────

  _onAtcClick(e) {
    e.preventDefault();
    const btn = e.currentTarget;

    // Voyado card: no embedded JSON yet — fetch product data on first click
    if (this._variants === null && this.dataset.productUrl) {
      this._fetchAndInit(btn);
      return;
    }

    if (!this._hasSizes) {
      // Single-variant or no size option — add directly
      this._addToCart(parseInt(btn.dataset.variantId, 10), btn);
      return;
    }

    // Toggle popup
    if (this._popup && !this._popup.hidden) {
      this._closePopup();
    } else {
      this._openPopup();
    }
  }

  _onDocClick(e) {
    if (!this._popup || this._popup.hidden) return;
    // Close if click lands outside both the component and the popup
    if (!this.contains(e.target) && !this._popup.contains(e.target)) {
      this._closePopup();
    }
  }

  _onKeyDown(e) {
    if (e.key !== 'Escape') return;
    if (!this._popup || this._popup.hidden) return;
    this._closePopup();
    this.querySelector('.product-card__atc')?.focus();
  }

  // ── Popup lifecycle ──────────────────────────────────────────────

  _openPopup() {
    if (!this._popup) {
      const popup = this._createPopup();
      if (!popup.children.length) return; // no sizes to show
      this._popup = popup;
      // Append to the media wrapper (a div), never the image anchor — a size
      // button click inside an <a> would navigate to the product instead of adding.
      const card = this.closest('.product-card') ?? this.closest('.cart-recs__card');
      const anchor = card?.querySelector('.product-card__media, .cart-recs__media');
      anchor?.appendChild(this._popup);
    }
    this._popup.hidden = false;
    this.querySelector('.product-card__atc')?.setAttribute('aria-expanded', 'true');
  }

  _closePopup() {
    if (this._popup) this._popup.hidden = true;
    this.querySelector('.product-card__atc')?.setAttribute('aria-expanded', 'false');
  }

  // ── Popup construction ───────────────────────────────────────────

  _getVariants() {
    if (!this._variants) {
      try {
        const el = this.querySelector('script[type="application/json"]');
        this._variants = el ? JSON.parse(el.textContent) : [];
      } catch {
        this._variants = [];
      }
    }
    return this._variants;
  }

  /**
   * Returns a Map of sizeLabel -> { variantId, available, lowStock }
   * filtered to the card's current colour (if the product has colours).
   * Prefers an available variant when multiple variants share a size label.
   */
  _buildSizeMap() {
    const variants = this._getVariants();
    const map = new Map();

    for (const v of variants) {
      // Filter by colour when the product has a colour option
      if (this._hasColors && v.options[this._colorOption] !== this._firstColor) continue;

      const sizeLabel = v.options[this._sizeOption];
      const entry = map.get(sizeLabel);

      // Keep first entry; upgrade to available variant if we find one
      if (!entry || (v.available && !entry.available)) {
        map.set(sizeLabel, {
          variantId: v.id,
          available: v.available,
          lowStock: v.available && typeof v.inventory_quantity === 'number' && v.inventory_quantity <= 1,
        });
      }
    }

    return map;
  }

  _createPopup() {
    const popup = document.createElement('div');
    popup.className = 'quick-buy__popup';
    popup.setAttribute('role', 'group');
    popup.setAttribute('aria-label', 'Select size');

    for (const [label, { variantId, available, lowStock }] of this._buildSizeMap()) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'quick-buy__size-btn';
      btn.textContent = label;
      btn.dataset.variantId = variantId;

      if (!available) {
        btn.classList.add('quick-buy__size-btn--unavailable');
        btn.setAttribute('aria-disabled', 'true');
      }
      if (lowStock) {
        btn.classList.add('quick-buy__size-btn--low-stock');
      }

      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (btn.getAttribute('aria-disabled') === 'true') return;
        this._addToCart(parseInt(btn.dataset.variantId, 10), btn);
      });

      popup.appendChild(btn);
    }

    return popup;
  }

  // ── Lazy-fetch init (Voyado cards) ───────────────────────────────────

  async _fetchAndInit(btn) {
    if (this._adding) return;
    this._adding = true;
    btn.setAttribute('aria-busy', 'true');
    try {
      const handle = new URL(this.dataset.productUrl, location.origin)
        .pathname.split('/products/')[1]?.split('/')[0];
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) throw new Error(`Product fetch ${res.status}`);
      const product = await res.json();

      this._variants = product.variants;
      const opts = (product.options ?? []).map(o => String(o).toLowerCase());
      const colorIdx = opts.findIndex(o => QB_COLOR_NAMES.includes(o));
      const sizeIdx  = opts.findIndex(o => QB_SIZE_NAMES.includes(o));

      this._hasColors   = colorIdx !== -1;
      this._colorOption = colorIdx !== -1 ? colorIdx : 0;
      this._sizeOption  = sizeIdx  !== -1 ? sizeIdx  : 0;

      // If no recognized size option but product has multiple variants, show the popup
      // using the first non-color option so the user can pick rather than getting a random variant.
      if (sizeIdx === -1 && product.variants.length > 1) {
        const fallback = opts.findIndex((_, i) => i !== colorIdx);
        this._sizeOption = fallback !== -1 ? fallback : 0;
        this._hasSizes = true;
      } else {
        this._hasSizes = sizeIdx !== -1;
      }

      const first = product.variants.find(v => v.available) ?? product.variants[0];
      if (!first) throw new Error('No variants found');
      this._firstColor = this._hasColors ? (first[`option${this._colorOption + 1}`] ?? '') : '';
      btn.dataset.variantId = String(first.id);

      this._adding = false;
      btn.removeAttribute('aria-busy');

      if (!this._hasSizes) {
        this._addToCart(first.id, btn);
      } else {
        this._openPopup();
      }
    } catch (err) {
      console.error('[QuickBuy] fetch failed:', err);
      this._adding = false;
      btn.removeAttribute('aria-busy');
    }
  }

  // ── Cart add ─────────────────────────────────────────────────────

  async _addToCart(variantId, btn) {
    if (this._adding) return;
    this._adding = true;
    btn.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      });
      if (!res.ok) throw new Error(`Cart add failed: ${res.status}`);

      const cartRes = await fetch('/cart.js');
      const cart    = await cartRes.json();

      this._closePopup();
      const card = this.closest('.product-card') ?? this.closest('.cart-recs__card');
      const voyadoTicket = card?.querySelector('[data-voyado-ticket]')?.dataset.voyadoTicket ?? null;
      TRYEvents.emit('cart:item-added', { item_count: cart.item_count, voyadoTicket });
    } catch (_) {
      // On error: restore button state silently.
      // The cart drawer shows its own error state if needed.
    } finally {
      this._adding = false;
      btn.removeAttribute('aria-busy');
    }
  }
}

customElements.define('quick-buy', QuickBuy);
